import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import { trackEvent } from '@/lib/faro';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import type { ToyCategory } from '@/types';

export default function ToysTab() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);

    const { data: categoriesData, isLoading: loadingToys } = useQuery({
        queryKey: ['toy-categories'],
        queryFn: () => api.get<{ data: ToyCategory[] }>('/toys'),
    });

    const { data: myToysData, isLoading: loadingMyToys } = useQuery({
        queryKey: ['my-toys'],
        queryFn: () => api.get<{ data: number[] }>('/my-toys'),
        staleTime: 0,
    });

    const categories = categoriesData?.data ?? [];
    const serverToyIds = myToysData?.data ?? [];
    const toySet = new Set(serverToyIds);

    const filteredCategories = useMemo(() => {
        let result = categories;

        if (activeCategory) {
            result = result.filter((c) => c.slug === activeCategory);
        }

        if (search) {
            const term = search.toLowerCase();
            result = result
                .map((cat) => ({
                    ...cat,
                    toys: cat.toys.filter((t) => t.name.toLowerCase().includes(term)),
                }))
                .filter((cat) => cat.toys.length > 0);
        }

        return result;
    }, [categories, activeCategory, search]);

    const toggleMutation = useMutation({
        mutationFn: (toyId: number) => {
            const next = new Set(toySet);
            if (next.has(toyId)) {
                next.delete(toyId);
            } else {
                next.add(toyId);
            }
            return api.put('/my-toys', { toy_ids: Array.from(next) });
        },
        onMutate: async (toyId: number) => {
            await queryClient.cancelQueries({ queryKey: ['my-toys'] });
            const previous = queryClient.getQueryData<{ data: number[] }>(['my-toys']);
            queryClient.setQueryData<{ data: number[] }>(['my-toys'], (old) => {
                if (!old) return old;
                const set = new Set(old.data);
                if (set.has(toyId)) {
                    set.delete(toyId);
                } else {
                    set.add(toyId);
                }
                return { data: Array.from(set) };
            });
            return { previous };
        },
        onError: (err: Error, _toyId, context) => {
            if (context?.previous) {
                queryClient.setQueryData(['my-toys'], context.previous);
            }
            toast(err.message);
        },
        onSettled: () => {
            trackEvent('toys_updated');
            queryClient.invalidateQueries({ queryKey: ['my-toys'] });
        },
    });

    if (loadingToys || loadingMyToys) {
        return <p className="text-sm text-slate-400">Loading toy inventory...</p>;
    }

    return (
        <div className="space-y-6">
            {/* Search and category filter */}
            <div className="space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search toy inventory..."
                        className="pl-9"
                    />
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                    <button
                        type="button"
                        onClick={() => setActiveCategory(null)}
                        className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                            !activeCategory ? 'bg-violet-700 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        All
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat.slug}
                            type="button"
                            onClick={() => setActiveCategory(cat.slug === activeCategory ? null : cat.slug)}
                            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                activeCategory === cat.slug ? 'bg-violet-700 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
            </div>

            <p className="text-xs text-slate-500">Select the toys you own or use. This will be visible on your profile.</p>

            {/* Toy categories */}
            {filteredCategories.map((category) => (
                <div key={category.id}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {category.name}
                    </h3>
                    <Card>
                        <CardContent className="p-0">
                            {category.toys.map((toy, idx) => (
                                <div
                                    key={toy.id}
                                    className={`flex items-center justify-between px-4 py-2.5 ${
                                        idx > 0 ? 'border-t border-slate-800/50' : ''
                                    }`}
                                >
                                    <span className="text-sm text-slate-200">{toy.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => toggleMutation.mutate(toy.id)}
                                        className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors ${
                                            toySet.has(toy.id) ? 'bg-violet-600' : 'bg-slate-700'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                                                toySet.has(toy.id) ? 'translate-x-5' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            ))}

            {filteredCategories.length === 0 && (
                <p className="text-center text-sm text-slate-500 py-8">No toys match your search.</p>
            )}
        </div>
    );
}
